import {
  StringValidator
} from "./chunk-P4GLTARJ.mjs";
import {
  AuthCodeNotFoundError,
  CsrfError,
  CsrfNotFoundError,
  EnvironmentNotGivenError,
  InvalidHttpTokenResponseError,
  InvalidURLSearchParamsError,
  MissingSearchCodeParamError,
  MissingSearchStateParamError,
  NonceError,
  StatusCode1xxError,
  StatusCode3xxError,
  StatusCode4xxError,
  StatusCode5xxError
} from "./chunk-RDVYLKWU.mjs";
import {
  errorMessages
} from "./chunk-KSIEOKOR.mjs";
import {
  Environment,
  getLoginUrl
} from "./chunk-XEDCQDS7.mjs";

// src/utils/validators.ts
import { decodeJwt, decodeState, verifyJwt } from "@inner-source/ciam-sdk";
function validateConfig(baseClass) {
  return class extends baseClass {
    constructor(...args) {
      const [{ stateParams, redirectUrl, clientId, environment }] = args;
      StringValidator.validateStringValue(
        stateParams.relyingParty,
        "relying-party"
      );
      StringValidator.validateStringValue(clientId, "clientId");
      StringValidator.validateStringValue(redirectUrl, "redirectUrl");
      StringValidator.validateUrl(redirectUrl);
      validateEnvironment(environment);
      super(...args);
    }
  };
}
function validateNonce(target, propertyKey, descriptor) {
  if (typeof descriptor.value !== "function") {
    throw new Error(errorMessages.NOT_A_FUNCTION_ERROR);
  }
  const originalMethod = descriptor.value;
  descriptor.value = function validateNonceMethod(data) {
    if (typeof data.id_token !== "string") {
      throw new Error(errorMessages.INVALID_ID_TOKEN);
    }
    const decodedNonce = decodeJwt(data.id_token);
    if (typeof decodedNonce.payload !== "object" || decodedNonce.payload === null || typeof decodedNonce.payload.nonce !== "string") {
      throw new Error(errorMessages.INVALID_NONCE_JWT_PAYLOAD);
    }
    const nonce = decodedNonce.payload.nonce;
    const instance = this;
    StringValidator.validateStringValue(nonce, "nonce");
    if (nonce !== instance.getNonce()) {
      throw new NonceError(errorMessages.INVALID_NONCE_DOES_NOT_MATCH);
    }
    return originalMethod.call(this, data);
  };
}
function validateResponseKeys(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validateResponse(data) {
    if (!data.access_token) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_ACCESS_TOKEN_RESPONSE_KEY
      );
    }
    if (!data.expires_in) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_EXPIRES_IN_RESPONSE_KEY
      );
    }
    if (!data.refresh_token) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_REFRESH_TOKEN_RESPONSE_KEY
      );
    }
    if (!data.token_type) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_TOKEN_TYPE_RESPONSE_KEY
      );
    }
    return originalMethod.call(this, data);
  };
}
function validateResponseKeysWithoutRT(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validateResponse(data) {
    if (!data.access_token) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_ACCESS_TOKEN_RESPONSE_KEY
      );
    }
    if (!data.expires_in) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_EXPIRES_IN_RESPONSE_KEY
      );
    }
    if (!data.token_type) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_TOKEN_TYPE_RESPONSE_KEY
      );
    }
    return originalMethod.call(this, data);
  };
}
var validParams = [
  "state",
  "code",
  "lastOp",
  "casl",
  "utm[utm_source]",
  "utm[utm_medium]",
  "utm[utm_campaign]",
  "utm[utm_term]",
  "utm[utm_content]",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content"
];
function validateURLSearchParams(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validatedMethod(search) {
    for (const key of search.keys()) {
      if (!validParams.includes(key)) {
        throw new InvalidURLSearchParamsError(`[${key}]: invalid Key.`);
      }
    }
    if (!search.get("code")) {
      throw new MissingSearchCodeParamError(
        errorMessages.MISSING_CODE_PARAM_ERROR
      );
    } else if (!search.get("state")) {
      throw new MissingSearchStateParamError(
        errorMessages.MISSING_STATE_PARAM_ERROR
      );
    }
    return originalMethod.call(this, search);
  };
}
function validateCsrf(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validatedMethod(search) {
    if (search.get("state")) {
      let state = search.get("state");
      try {
        state = decodeURIComponent(state);
        state = state.replace(/\s/g, "+");
      } catch (e) {
      }
      const { csrf } = decodeState(state);
      const instance = this;
      StringValidator.validateStringValue(csrf, "csrf");
      if (csrf !== instance.getCsrf()) {
        throw new CsrfError(errorMessages.INVALID_CSRF_DOES_NOT_MATCH);
      }
    }
    return originalMethod.call(this, search);
  };
}
function validateAuthCodeVerifierKey(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validate() {
    const codeVerifier = originalMethod.call(this);
    if (!codeVerifier) {
      throw new AuthCodeNotFoundError(
        errorMessages.AUTH_CODE_VERIFIER_KEY_NOT_FOUND
      );
    }
    return codeVerifier;
  };
}
function validateResponseStatus(target, propertyKey, descriptor) {
  if (typeof descriptor.value !== "function") {
    throw new Error(errorMessages.NOT_A_FUNCTION_ERROR);
  }
  const originalMethod = descriptor.value;
  descriptor.value = function validate(HTTPRawResponse) {
    if (HTTPRawResponse.status >= 100 && HTTPRawResponse.status < 200) {
      throw new StatusCode1xxError(errorMessages.INVALID_1XX_ERRORS);
    } else if (HTTPRawResponse.status >= 300 && HTTPRawResponse.status < 400) {
      throw new StatusCode3xxError(errorMessages.INVALID_3XX_ERRORS);
    } else if (HTTPRawResponse.status >= 400 && HTTPRawResponse.status < 500) {
      throw new StatusCode4xxError(errorMessages.INVALID_4XX_ERRORS);
    } else if (HTTPRawResponse.status >= 500 && HTTPRawResponse.status < 600) {
      throw new StatusCode5xxError(errorMessages.INVALID_5XX_ERRORS);
    }
    return originalMethod.call(this, HTTPRawResponse);
  };
}
function validateRequest(target, propertyKey, descriptor) {
  if (typeof descriptor.value !== "function") {
    throw new Error(errorMessages.NOT_A_FUNCTION_ERROR);
  }
  const originalMethod = descriptor.value;
  descriptor.value = function validate(requestUrl, data, options = {}) {
    StringValidator.validateStringValue(requestUrl, "requestUrl");
    StringValidator.validateUrl(requestUrl);
    return originalMethod.call(this, requestUrl, data, options);
  };
}
function validateEnvironment(environment) {
  if (!Object.values(Environment).includes(environment)) {
    throw new EnvironmentNotGivenError(errorMessages.ENVIRONMENT_NOT_PRESENT);
  }
}
async function validateToken(accessToken, env, logManager) {
  const jwkUrl = `${getLoginUrl(env)}/admin/v1/SigningCert/jwk`;
  try {
    const validJwt = await verifyJwt(jwkUrl, accessToken);
    return validJwt;
  } catch (error) {
    logManager.log(
      "ERROR" /* ERROR */,
      `Error during token validation: ${error.message}`
    );
    return false;
  }
}
function validateSessionCsrf(state, logManager) {
  let csrf;
  try {
    let givenState = state;
    try {
      givenState = decodeURIComponent(state);
      givenState = givenState.replace(/\s/g, "+");
    } catch (_e) {
    }
    csrf = decodeState(givenState).csrf;
  } catch (error) {
    logManager.log(
      "ERROR" /* ERROR */,
      `Error while decoding csrf: ${error.message}`
    );
    throw error;
  }
  const storedCsrf = sessionStorage.getItem("csrfKey");
  if (storedCsrf === null) {
    throw new CsrfNotFoundError("Csrf not found in sessionStorage.");
  }
  if (csrf !== storedCsrf) {
    logManager.log(
      "ERROR" /* ERROR */,
      errorMessages.INVALID_CSRF_DOES_NOT_MATCH
    );
    throw new CsrfError(errorMessages.INVALID_CSRF_DOES_NOT_MATCH);
  }
}
function validateSessionNonce(idToken, logManager) {
  if (idToken) {
    const decodedNonce = decodeJwt(idToken);
    const storedNonce = sessionStorage.getItem("pcidNonceKey");
    if (storedNonce === null)
      throw new NonceError("Nonce not found in sessionStorage.");
    if (decodedNonce.payload.nonce !== storedNonce) {
      logManager.log(
        "ERROR" /* ERROR */,
        errorMessages.INVALID_NONCE_DOES_NOT_MATCH
      );
      throw new NonceError(errorMessages.INVALID_NONCE_DOES_NOT_MATCH);
    }
  }
}

export {
  validateConfig,
  validateNonce,
  validateResponseKeys,
  validateResponseKeysWithoutRT,
  validateURLSearchParams,
  validateCsrf,
  validateAuthCodeVerifierKey,
  validateResponseStatus,
  validateRequest,
  validateEnvironment,
  validateToken,
  validateSessionCsrf,
  validateSessionNonce
};
